/**
 * (c) LBS logics
 *
 *     Mobile Business Technology GmbH
 *     4020 Linz, Austria
 *
 *     www.lbs-logics.com
 *
 *     created on 20190902 by Jan Brunner
 *     all rights reserved.
 */

import { Injectable } from '@angular/core';
import { CargoItemsService } from '../cargo-item.service';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { BehaviorSubject } from 'rxjs';
import { RemoteActionService } from 'src/app/_services/remote-action/logic/remote-action.service';
import { AuthenticationService } from 'src/app/_services/authentication/authentication.service';
import { CargoItemsDaoService } from '../../dao/cargo-item-dao.service';
import { IMSId } from 'src/app/_model/ims-object';
import { AppService } from 'src/app/_services/app-service/logic/app.service';
import { CredentialsService } from 'src/app/_services/credentials/logic/credentials.service';
import { LocationService } from 'src/app/_services/location/location.service';
import { CargoItem, CargoItemDTO } from 'src/app/_model/cargo-item';
import { CargoItemType } from 'src/app/_model/cargo-item-types';
import { Tour } from 'src/app/_model/tour';
import { Servicejob } from 'src/app/_model/servicejob';
import { Branch } from 'src/app/_model/branches';
import { LocalStatusService } from 'src/app/_services/local-status/local-status.service';
import { InitializeStatus } from 'src/app/_services/initialize/initialize-aware';
import { APILevel } from 'src/app/_services/sync/api-level';

/**
 * Loads CargoItems
 */
@Injectable()
export class CargoItemsLogicService implements CargoItemsService {
  public cargoItems$ = new BehaviorSubject<CargoItem[]>([]);
  public initializeStatus$: BehaviorSubject<InitializeStatus> =
    new BehaviorSubject(InitializeStatus.NOT_INITIALIZED);

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private cargoItemsDaoService: CargoItemsDaoService,
    private authenticationService: AuthenticationService,
    private localStatusService: LocalStatusService,
  ) {
    this.initialize();
  }

  // Public Function
  public getCargoItemById(id: IMSId): CargoItem {
    if (id === undefined) {
      return undefined;
    }

    return this.cargoItems$.getValue().find((cargoItem) => {
      if (
        cargoItem.id.imsId === id.imsId &&
        cargoItem.id.secondaryId === id.secondaryId
      ) {
        return true;
      }
      return false;
    });
  }

  public getCargoItemByExtId(extid: string): CargoItem {
    if (extid === undefined) {
      return undefined;
    }

    return this.cargoItems$.getValue().find((cargoItem) => {
      if (cargoItem.extId === extid && cargoItem.extId === extid) {
        return true;
      }
      return false;
    });
  }

  public getNotAssignedCargoItemByTourId(id: IMSId): CargoItem[] {
    if (id === undefined) {
      return [];
    }

    return this.cargoItems$.getValue().filter((cargoItem) => {
      if (
        cargoItem.tour.id.imsId === id.imsId &&
        cargoItem.tour.id.secondaryId === id.secondaryId &&
        cargoItem.servicejob == undefined
      ) {
        return true;
      }
      return false;
    });
  }

  public getReturnedCargoItemTypeAmountByTourId(
    cargoItemTypeId: IMSId,
    tourId: IMSId,
  ): number {
    if (tourId === undefined || cargoItemTypeId === undefined) {
      return 0;
    }
    return this.cargoItems$
      .getValue()
      .filter((cargoItem) => {
        if (
          cargoItem.tour.id.imsId === tourId.imsId &&
          cargoItem.tour.id.secondaryId === tourId.secondaryId &&
          cargoItem.cargoItemType.id.imsId === cargoItemTypeId.imsId &&
          cargoItem.cargoItemType.id.secondaryId === cargoItemTypeId.secondaryId
        ) {
          return true;
        }
        return false;
      })
      .map((cargoItem) => cargoItem.amtreturn ?? 0)
      .reduce((prev, cur) => prev + cur, 0);
  }

  public getAllCargoItemsOnTour(id: IMSId): CargoItem[] {
    if (id === undefined) {
      return [];
    }

    return this.cargoItems$.getValue().filter((cargoItem) => {
      if (
        cargoItem.tour.id.imsId === id.imsId &&
        cargoItem.tour.id.secondaryId === id.secondaryId
      ) {
        return true;
      }
      return false;
    });
  }

  public getAllCargoItemsForServiceJobs(ids: IMSId[]): CargoItem[] {
    if (ids === undefined) {
      return [];
    }

    return this.cargoItems$.getValue().filter((cargoItem) => {
      return (
        ids.findIndex((it) => {
          if (
            cargoItem.servicejob?.id?.imsId === it.imsId &&
            cargoItem.servicejob?.id?.secondaryId === it.secondaryId
          ) {
            return true;
          }
          return false;
        }) >= 0
      );
    });
  }

  public getAllCargoItemsForServiceJob(id: IMSId): CargoItem[] {
    if (id === undefined) {
      return [];
    }

    return this.cargoItems$.getValue().filter((cargoItem) => {
      if (
        cargoItem.servicejob?.id?.imsId === id?.imsId &&
        cargoItem.servicejob?.id?.secondaryId === id?.secondaryId
      ) {
        return true;
      }
      return false;
    });
  }

  public async updateCargoItems(cargoItemsDto: CargoItemDTO[]): Promise<void> {
    this.logger.log(
      this.constructor.name,
      '- Received new cargo items from other IMS component',
      cargoItemsDto,
    );

    try {
      let cargoItems: CargoItem[] = [];

      for (const cargoItemDto of cargoItemsDto) {
        let localStatus =
          await this.localStatusService.loadLocalCargoItemStatus({
            imsId: cargoItemDto.id,
            secondaryId: undefined,
            objRef: 'ims/CargoItem',
          });
        cargoItems.push(
          CargoItem.fromDto(cargoItemDto, undefined, localStatus),
        );
      }

      await this.cargoItemsDaoService.saveCargoItems(cargoItems);
      await this.cargoItems$.next(cargoItems);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error parsing CargoItems: ',
        error.message,
      );
      throw error;
    }
  }

  public async updateCargoItem(cargoItem: CargoItem) {
    let cargoItems = this.cargoItems$.getValue();
    let updated = false;
    for (let ci of cargoItems) {
      if (
        (ci.id.imsId && ci.id.imsId === cargoItem.id.imsId) ||
        (ci.id.secondaryId && ci.id.secondaryId === cargoItem.id.secondaryId)
      ) {
        ci = cargoItem;
        updated = true;
      }
    }

    if (!updated) {
      let newCargoItem =
        await this.cargoItemsDaoService.createCargoItem(cargoItem);
      cargoItems.push(newCargoItem);
    }
    await this.cargoItemsDaoService.saveCargoItems(cargoItems);
    this.cargoItems$.next(cargoItems);
  }

  public async createCargoItemForTour(cargoItemDto: CargoItemDTO, tour: Tour) {
    let cargoItems = this.cargoItems$.getValue();
    let updated = false;
    for (let ci of cargoItems) {
      if (
        ci.extId === cargoItemDto.extid &&
        ci.tour.id.imsId === cargoItemDto.tour.id
      ) {
        ci.amtopen = ci.amtopen + cargoItemDto.amtopen;
        updated = true;
      }
    }

    if (!updated) {
      let newCargoItem = await this.cargoItemsDaoService.createCargoItem(
        CargoItem.fromDto(cargoItemDto),
      );
      cargoItems.push(newCargoItem);
    }
    await this.cargoItemsDaoService.saveCargoItems(cargoItems);
    this.cargoItems$.next(cargoItems);
  }

  public async createCargoItemFromDto(cargoItemDto: CargoItemDTO) {
    this.logger.log(
      this.constructor.name,
      'create cargoitem from dto',
      cargoItemDto,
    );

    if (cargoItemDto === undefined) {
      return;
    }

    // Create a new CargoItem for the Sjob
    let sjobCargoItem = CargoItem.fromDto(cargoItemDto);

    let cargoItems = this.cargoItems$.getValue();
    cargoItems.push(sjobCargoItem);
    await this.cargoItemsDaoService.saveCargoItems(cargoItems);
    this.cargoItems$.next(cargoItems);
  }

  public async assignTourCargoItemtoSjob(
    cargoItem: CargoItem,
    sjob: Servicejob,
    branch: Branch,
    assignAmt: number,
    amtReturn: number,
  ) {
    this.logger.log(
      this.constructor.name,
      'assign cargoitem',
      cargoItem.name,
      'with amtassign',
      assignAmt,
      'and amtreturn',
      amtReturn,
      'to sjob',
      sjob.name,
    );
    if (sjob === undefined || cargoItem === undefined) {
      return;
    }

    cargoItem.amtopen = cargoItem.amtopen - (assignAmt ?? 0) + (amtReturn ?? 0);

    await this.updateCargoItem(cargoItem);

    await this.createCargoItemFromDto({
      id: undefined,
      secondaryid: undefined,
      extid: cargoItem.extId,
      branch: branch
        ? {
            id: branch.id?.imsId,
            label: branch.label,
          }
        : undefined,
      itemvalue: cargoItem.itemvalue,
      label: cargoItem.label,
      name: cargoItem.name,
      serialnumber: cargoItem.serialNumber,
      servicejob: sjob
        ? {
            id: sjob.id?.imsId,
            label: sjob.label,
          }
        : undefined,
      sortkey: undefined,
      tour: {
        id: cargoItem.tour?.id?.imsId,
        label: cargoItem.tour?.label || '',
      },
      amtdlv: assignAmt,
      amtopen: 0,
      amtreturn: amtReturn,
      cargoitemtype: {
        id: cargoItem.cargoItemType?.id?.imsId,
        label: cargoItem.cargoItemType?.label,
      },
    });
  }

  // Resetable
  public async reset() {
    await this.cargoItemsDaoService.reset();
    await this.loadLocalCargoItems();
  }

  public async clear() {
    await this.cargoItemsDaoService.clear();
    this.cargoItems$.next([]);
  }

  // Syncable
  public async sync(apiLevel: APILevel, syncKey: string): Promise<void> {}

  public async presync(): Promise<void> {}

  public async postsync(): Promise<void> {}

  // InitializeAware
  async initialize(): Promise<void> {
    this.initializeStatus$.next(InitializeStatus.INITIALIZING);
    try {
      await this.loadLocalCargoItems();
      this.initializeStatus$.next(InitializeStatus.INITIALIZED);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local data: ',
        error?.message,
      );
      this.initializeStatus$.next(InitializeStatus.FAILED_INITIALIZE);
    }
  }

  // Private Functions
  private async loadLocalCargoItems() {
    try {
      this.logger.log(this.constructor.name, '- Load local CargoItems');
      let cargoItems: CargoItem[] =
        await this.cargoItemsDaoService.getCargoItems();
      this.logger.log(
        this.constructor.name,
        '- Loaded local CargoItems',
        cargoItems.length,
      );
      this.cargoItems$.next(cargoItems);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local cargo items: ',
        error.message,
      );
      throw error;
    }
  }

  async initializeCompleted(): Promise<void> {}
}
